import CachedIcon from "@mui/icons-material/Cached";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import prettyBytes from "pretty-bytes";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { deleteFile } from "../../../api/file/deleteFile";
import { downloadFile } from "../../../api/file/downloadFile";
import { getStageFiles } from "../../../api/file/getStageFiles";
import { getStageRole } from "../../../api/stage";
import { saveAwardReport } from "../../../api/stage/awardReport/saveAwardReport";
import apm from "../../../apm";
import { useTranslationHook } from "../../../hooks";
import { setLoading } from "../../../store/UI";
import { ProjectFile } from "../../../types/model/file";
import { Procedure, ProcedureRole } from "../../../types/model/procedure";
import {
  AwardReport,
  AwardReportRequest,
  AwardReportRequestSchema,
  Stage,
  StageRole,
} from "../../../types/model/stage";
import { formatDate } from "../../../utils";
import DescriptionEditor from "../../DescriptionEditor/DescriptionEditor";
import { StageFileUpload } from "../StageFileUpload";
import WinnerSelector from "./WinnerSelector";

interface AwardReportProps {
  stage: Stage;
  awardReport: AwardReport | undefined;
  procedureRole: ProcedureRole;
  accountId: number;
  projectId: number;
  activityId: number;
  procedureId: number;
  procedure: Procedure;
}

const AwardReports = (props: AwardReportProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const {
    stage,
    awardReport,
    procedureRole,
    accountId,
    projectId,
    activityId,
    procedureId,
    procedure,
  } = props;
  const [stageRole, setStageRole] = useState<StageRole>();
  const [updatedDescription, setUpdatedDescription] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<ProjectFile[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filteredFiles, setFilteredFiles] = useState<ProjectFile[]>([]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const fetchStageRole = async () => {
    try {
      const data = await getStageRole(projectId, activityId, procedureId, stage.id);
      setStageRole(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownload = async (fileId: number, filename: string) => {
    dispatch(setLoading(true));
    try {
      await downloadFile(fileId, filename);
    } catch (error) {
      console.error("Download error:", error);
    }
    dispatch(setLoading(false));
  };

  const handleDeleteExistingFile = async (fileId: number) => {
    try {
      await deleteFile(fileId);
      fetchExistingFiles();
    } catch (error) {
      console.error("Deleting error", error);
    }
  };

  const handleOnFileUpdated = () => {
    fetchExistingFiles();
  };

  const fetchExistingFiles = async () => {
    getStageFiles(stage.id)
      .then((response) => {
        setUploadedFiles(response);
        setFilteredFiles(response);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleCancelAction = () => {
    setEditMode(false);
  };

  const handleEditAction = () => {
    setEditMode(true);
  };

  const handleSaveAction = (values: any) => {
    try {
      let newQuestion: AwardReportRequest = AwardReportRequestSchema.parse({
        description: values.field1,
      });
      saveAwardReport(projectId, activityId, procedureId, stage.id, newQuestion)
        .then((response) => {
          setUpdatedDescription(
            response.description ? response.description : ""
          );
          enqueueSnackbar(t("stages.award-reports.saved"), {
            variant: "success",
          });
          setEditMode(false);
        })
        .catch((error) => {
          apm.captureError(error);
          enqueueSnackbar(t("stages.award-reports.error-save"), {
            variant: "error",
          });
        });
    } catch (err: unknown) {
      if (err instanceof Error) {
        apm.captureError(err);
      }
      enqueueSnackbar("stages.award-reports.error-save", { variant: "error" });
    }
  };

  useEffect(() => {
    setUpdatedDescription(
      awardReport?.description ? awardReport.description : ""
    );
    fetchExistingFiles();
    fetchStageRole();
  }, []);

  useEffect(() => {
    const lower = search.toLowerCase();
    const filtered = uploadedFiles.filter((file) =>
      file.originalName
        ? file.originalName.toLowerCase().includes(lower)
        : false
    );
    setFilteredFiles(filtered);
    setPage(0);
  }, [search, uploadedFiles]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("generic.stage") + " - " + t(stage.stageTypeLabel)}
      </Typography>

      <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label={t("generic.details")} />
        <Tab label={t("generic.files")} />
        <Tab label={t("tender.winner")} />
      </Tabs>

      {tabIndex === 0 && (
        <Box>
          <Formik
            enableReinitialize
            initialValues={{
              field1: updatedDescription || undefined,
            }}
            onSubmit={(values) => {
              handleSaveAction(values);
            }}
            validationSchema={Yup.object().shape({
              field1: Yup.string(),
            })}
          >
            {(props) => {
              const {
                handleSubmit,
              } = props;
              return (
                <form onSubmit={handleSubmit}>
                  <Grid2 container spacing={3}>
                    {procedureRole.isManager && !procedure.canceled && !procedure.closed && (
                      <Grid2 size={12} justifyItems={"right"}>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          {!editMode && (
                            <Tooltip title={t("generic.edit")}>
                              <IconButton onClick={handleEditAction}>
                                <EditIcon sx={{ fontSize: 28 }} />
                              </IconButton>
                            </Tooltip>
                          )}

                          {editMode && (
                            <>
                              <Tooltip title={t("generic.cancel")}>
                                <IconButton
                                  onClick={handleCancelAction}
                                  sx={{ color: "red" }}
                                >
                                  <CloseIcon sx={{ fontSize: 28 }} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title={t("generic.save")}>
                                <IconButton
                                  type="submit"
                                  sx={{ color: "green" }}
                                >
                                  <SaveIcon sx={{ fontSize: 28 }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </Grid2>
                    )}
                    <Grid2 size={12}>
                      {/*Description*/}
                      <Box
                        sx={{
                          mb: 2,
                          "& .ProseMirror": {
                            height: "30dvh",
                            overflowY: "auto",
                          },
                        }}
                      >
                        <DescriptionEditor
                          initialText={updatedDescription}
                          isTender={procedureRole.isTender}
                          isEditMode={editMode}
                          onChange={(newValue) => {
                            props.setFieldValue("field1", newValue);
                          }}
                        />
                      </Box>
                    </Grid2>
                  </Grid2>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {tabIndex === 1 && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <TextField
                fullWidth
                label={t("generic.search") ?? "Search"}
                variant="outlined"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mr: 2 }}
              />
              <Tooltip title={t("generic.refresh")}>
                <IconButton onClick={fetchExistingFiles}>
                  <CachedIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Tabela de arquivos */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("generic.file-name") ?? "File name"}
                    </TableCell>
                    <TableCell>{t("generic.created")}</TableCell>
                    <TableCell>{t("generic.account")}</TableCell>
                    <TableCell>{t("generic.size")}</TableCell>
                    <TableCell align="right">
                      {t("generic.actions") ?? "Actions"}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFiles
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((file) => (
                      <TableRow key={file.id}>
                        <TableCell>{file.originalName}</TableCell>
                        <TableCell>
                          {file.createdOn
                            ? formatDate(file.createdOn.toString())
                            : ""}
                        </TableCell>
                        <TableCell>{file.accountName}</TableCell>
                        <TableCell>{prettyBytes(file.size)}</TableCell>
                        <TableCell align="right">
                          {(stageRole?.isManager ||
                            stageRole?.isTender ||
                            (stageRole?.isOwner && stage.closed)) && (
                            <IconButton
                              onClick={() =>
                                handleDownload(
                                  file.id,
                                  file.originalName ? file.originalName : ""
                                )
                              }
                              color="primary"
                              size="small"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          )}
                          {stageRole!.isManager && !procedure.canceled && !procedure.closed && (
                            <IconButton
                              onClick={() => handleDeleteExistingFile(file.id)}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Paginação */}
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={filteredFiles.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number.parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>

          {stageRole!.isManager && !procedure.canceled && !procedure.closed && (
            <StageFileUpload
              accountId={accountId}
              projectId={projectId}
              activityId={activityId}
              procedureId={procedureId}
              stageId={stage.id}
              onFilesUpdated={handleOnFileUpdated}
              procedure={procedure}
            />
          )}
        </>
      )}
      {tabIndex === 2 && (
        <WinnerSelector
          accountId={accountId}
          projectId={projectId}
          activityId={activityId}
          procedureId={procedureId}
          procedureRole={procedureRole}
          procedure={procedure}
        />
      )}
    </Box>
  );
};

export default AwardReports;
